import { useState, useEffect, useCallback } from 'react';
import { supabase, signInWithEmail, signUpWithEmail, signOut as supabaseSignOut, onAuthStateChange } from '../lib/supabase';

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Listen for auth changes
  useEffect(() => {
    // Get initial session
    const getSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setUser(session?.user || null);
      } catch (err) {
        console.error('Error getting session:', err);
      } finally {
        setLoading(false);
      }
    };

    getSession();

    // Subscribe to auth changes
    const { data: { subscription } } = onAuthStateChange((event, session) => {
      setUser(session?.user || null);
      setLoading(false);
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  // Sign in
  const signIn = useCallback(async (email, password) => {
    setLoading(true);
    setError(null);
    
    try {
      const { data, error: signInError } = await signInWithEmail(email, password);
      
      if (signInError) {
        setError(signInError.message);
        return { success: false, error: signInError.message };
      }
      
      setUser(data.user);
      return { success: true, user: data.user };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Sign up
  const signUp = useCallback(async (email, password, displayName = '') => {
    setLoading(true);
    setError(null);
    
    try {
      const { data, error: signUpError } = await signUpWithEmail(email, password, {
        display_name: displayName,
      });
      
      if (signUpError) {
        setError(signUpError.message);
        return { success: false, error: signUpError.message };
      }
      
      return { success: true, user: data.user };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Sign out
  const signOut = useCallback(async () => {
    setLoading(true);
    
    try {
      const { error: signOutError } = await supabaseSignOut();
      
      if (signOutError) {
        setError(signOutError.message);
        return { success: false, error: signOutError.message };
      }
      
      setUser(null);
      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Get display name
  const getDisplayName = useCallback(() => {
    if (!user) return null;
    return user.user_metadata?.display_name || user.email?.split('@')[0] || 'משתמש';
  }, [user]);

  return {
    user,
    loading,
    error,
    signIn,
    signUp,
    signOut,
    getDisplayName,
    isAuthenticated: !!user,
  };
};
